import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
import { usercontainerActions } from '../actions';
import { containerLanguage, PLATFORM_NAMES, USERCONTAINER_UNKNOWN_OUTCOME } from '../constants';
import { DraggableLoadingPopup } from './SpaceItem';

// "Your Social News cards": add a themed card, reorder the whole card list,
// remove a card you created. Built on the same dialog idiom as
// DraggableFeedConfigPopup (CustomDiscussionPagination.js) and the ~10
// Draggable*Popup components in SpaceItem.js - MUI Dialog + a local
// PaperComponent wrapping react-draggable.
//
// The rows themselves are sortable with @dnd-kit; this file is the first (and
// only) consumer of @dnd-kit in the app.

// Client-side mirrors of the backend's own title rules, pre-enforced so a
// predictably invalid title does not cost a round trip (POST /container can
// take up to a minute - see below).
const MAX_TITLE = 40;

// There is no fixed cap any more. Each user has their OWN server-owned
// allowance, read from usercontainer.themes = { used, allowed }: `allowed` is
// what that account may have, `used` is how many THEME rows it has in EVERY
// language together. Both come from the same responses that carry the card list
// (/mycontainers, POST /container, POST /container/delete), so the gate and the
// copy below are driven by the server's numbers, not by a guess made here.
//
// This constant is the fallback for ONE case only: no response has ever carried
// `themes` at all - an old or rolled-back backend - in which case the allowance
// is unknown. The gate and the counter line then reproduce the pre-allowance
// behaviour exactly - cap of 10 against the current language's count, and the
// old counter wording byte for byte. The 409 notice is the one exception: its
// no-allowance wording is new, because the old string named a maximum of 10
// that the frontend has no business asserting once the cap is server-owned.
// It is NOT a default allowance and must never be used while `allowance` is present, even
// when the numbers it carries look surprising (`used` above `allowed` is a
// normal state for an account whose allowance was lowered).
const FALLBACK_THEME_CEILING = 10;

// Written as a charCode scan rather than a regex on purpose: a character class
// containing control characters trips eslint's no-control-regex (a warning in
// eslint-config-react-app).
function hasControlCharacter(value) {
    for (let i = 0; i < value.length; i++) {
        const code = value.charCodeAt(i);
        if (code < 32 || code === 127) {
            return true;
        }
    }
    return false;
}

// -> a user-facing message, or null when the title is acceptable.
function titleProblem(trimmed) {
    if (trimmed.length === 0) {
        return 'Please type a theme first.';
    }
    if (trimmed.length > MAX_TITLE) {
        return 'Please use ' + MAX_TITLE + ' characters or fewer.';
    }
    if (hasControlCharacter(trimmed)) {
        return 'Please remove the special characters from your theme.';
    }
    // Case-insensitive, exactly like the backend's reserved-name check, so the
    // user is told before a round trip rather than after one.
    if (PLATFORM_NAMES.some(n => n.toLowerCase() === trimmed.toLowerCase())) {
        return '"' + trimmed + '" is one of the built-in cards. Please choose another theme.';
    }
    return null;
}

// The always-visible line under the Add row. Five branches, because the two
// server numbers can sit in any relation to one another:
//
//  A used > allowed  - the allowance was lowered below what the account already
//                      has. A NORMAL state, not an error: nothing is deleted,
//                      the user simply cannot add until they are back under it.
//  B used === allowed - at the allowance.
//  C used < allowed   - room to spare, and says how much.
//  D allowed === 0    - this account may not have custom cards at all. (used
//                      above 0 with allowed 0 is branch A, so reaching here
//                      means used is 0 too.)
//  E no allowance ever received - the pre-allowance copy, word for word, so an
//                      old or rolled-back backend looks exactly as it did.
//
// `used` counts every language; the list on screen shows one. Where those two
// disagree the difference is named rather than left to look like a bug - "you
// have 3" above a list of 2 is otherwise unexplainable to the user.
function allowanceSummary(allowance, allowed, used, themeCount, language) {
    if (!allowance) {
        // Branch E. `used >= allowed` here is `themeCount >= 10` by
        // construction (see the derivations in the component), so this is the
        // old `atLimit` test and the old two strings, unchanged.
        return used >= allowed
            ? 'You have reached the maximum of ' + FALLBACK_THEME_CEILING + ' custom cards. Remove one to add another.'
            : 'You have ' + themeCount + ' custom ' + (themeCount === 1 ? 'card' : 'cards') + ' in ' + language + '. You can have up to ' + FALLBACK_THEME_CEILING + ' in total, counting every language together.';
    }

    const elsewhere = Math.max(0, used - themeCount);
    const suffix = elsewhere === 0
        ? ''
        : elsewhere === 1
            ? ' One of them is in another language, so it is not in this list.'
            : ' ' + elsewhere + ' of them are in other languages, so they are not in this list.';

    if (used > allowed) {
        // Branch A. Pluralise on `used` here, not on `allowed`: this branch is
        // the one where they differ, and the noun counts what the user holds.
        return 'You have ' + used + ' custom ' + (used === 1 ? 'card' : 'cards')
            + '. Your account now allows ' + allowed
            + '. You keep ' + (used === 1 ? 'it' : 'all of them')
            + ', but you cannot add another until you have fewer than ' + allowed + '.' + suffix;
    }
    if (allowed === 0) {
        // Branch D. No suffix: there is nothing to be elsewhere.
        return 'Your account does not currently allow custom cards.';
    }
    if (used === allowed) {
        return 'You have ' + used + ' of the ' + allowed + ' custom ' + (allowed === 1 ? 'card' : 'cards')
            + ' your account allows. Remove one to add another.' + suffix;
    }
    // Branch C. Pluralise on `allowed` to match branch B - the noun belongs to
    // "the N custom card(s) your account allows", not to `used`.
    return 'You have ' + used + ' of the ' + allowed + ' custom ' + (allowed === 1 ? 'card' : 'cards')
        + ' your account allows. You can add ' + (allowed - used) + ' more.' + suffix;
}

// A STABLE row id, never the array index: dnd-kit animates and matches rows by
// id, so an index-based id makes it move the wrong row on reorder.
//
// Live PLATFORM entries carry only { kind, name, position } - no id at all -
// so theirs is synthesised from kind + name. THEME entries use their backend
// id, prefixed so a numeric id can never collide with a platform key
// ("THEME#42" vs "PLATFORM:Twitter"). Both halves are unique by construction:
// the server dedups platform names within a list, and a backend id is unique
// per row.
function rowId(container) {
    if (container.kind === "THEME" && container.id !== undefined && container.id !== null && container.id !== '') {
        return "THEME#" + container.id;
    }
    return container.kind + ":" + container.name;
}

// The local display model: one flat array of row descriptors in display order.
// containerId is kept alongside because POST /container/delete needs the
// backend id, and parsing it back out of the row id would be worse.
function toRows(containers) {
    const rows = [];
    const seen = {};
    (containers || []).forEach((c, index) => {
        let id = rowId(c);
        if (seen[id]) {
            // Should be unreachable (see rowId). Duplicate ids make dnd-kit
            // mis-animate rather than fail, which is exactly the kind of bug
            // that gets blamed on "drag and drop being flaky", so say so and
            // still force uniqueness.
            console.error('CardConfigPopup: duplicate card row id "' + id + '" - forcing it unique');
            id = id + "~" + index;
        }
        seen[id] = true;
        rows.push({
            id: id,
            kind: c.kind,
            name: c.name,
            containerId: c.kind === "THEME" ? c.id : undefined
        });
    });
    return rows;
}

// The order as a single comparable string, for the dirty flag.
function orderKey(rows) {
    return rows.map(r => r.id).join('|');
}

// Only ever the machine-readable error key. NOT body.message (prefixed at
// runtime with a random "[Error number: ffnpgtd]") and NOT body.status (the
// enum NAME string, e.g. "BAD_REQUEST", not a number).
function firstError(body) {
    if (body && Array.isArray(body.errors) && body.errors.length > 0) {
        return body.errors[0];
    }
    return undefined;
}

const useCardConfigStyles = makeStyles(theme => ({
    paper: {
        overflowY: 'hidden',
        overflowX: 'hidden',
        width: 320,
        '@media (min-width: 600px) and (max-width: 800px)': {
            width: 500
        },

        '@media (min-width: 800px)': {
            width: 600
        }
    },
    customizedButton: {
        backgroundColor: '#007bff',
        color: '#fff',
        float: 'right',
    },
}));

// The `cancel` selector is what keeps the two drag systems apart: without it
// the dialog's own dragger would also claim pointer/touch events that start
// inside DialogContent, where the sortable rows live. All 7 dialog draggers in
// this repo carry it. The sortable rows must therefore stay inside
// DialogContent and never move into DialogTitle.
function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

// One row of the ordered list: drag handle left, name centre, PLATFORM/THEME
// marker, then up / down / delete.
const CardRow = ({ row, index, total, narrow, dark, busy, deleteDisabled, confirming, onMove, onAskDelete, onCancelDelete, onConfirmDelete }) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });

    // x is pinned to 0 so the dragged row only ever tracks the pointer
    // vertically. @dnd-kit/modifiers (restrictToVerticalAxis) is not installed
    // and this is the same effect without adding a dependency.
    const style = {
        transform: transform ? CSS.Translate.toString({ x: 0, y: transform.y }) : undefined,
        transition: transition,
        backgroundColor: isDragging ? (dark ? '#002147' : '#e9eef5') : 'transparent',
        borderRadius: 6,
        opacity: isDragging ? 0.95 : 1
    };

    const isTheme = row.kind === "THEME";
    const buttonSize = narrow ? 'small' : 'medium';
    const iconSize = narrow ? 'small' : 'medium';
    const buttonStyle = narrow ? { padding: 2, color: dark ? '#fff' : '#000' } : { color: dark ? '#fff' : '#000' };

    return (
        <div ref={setNodeRef} style={style}>
            <Stack direction="row" alignItems="center" spacing={0} style={{ paddingTop: 2, paddingBottom: 2 }}>
                <IconButton
                    {...attributes}
                    {...listeners}
                    ref={setActivatorNodeRef}
                    size={buttonSize}
                    disabled={busy}
                    aria-label={"Reorder " + row.name}
                    title="Drag to reorder"
                    // touch-action: none is required for pointer-event dragging
                    // on touch devices. It is on the HANDLE only, so a finger
                    // anywhere else on a 20+ row list still scrolls normally.
                    style={{ ...buttonStyle, touchAction: 'none', cursor: 'grab' }}
                >
                    <DragIndicatorIcon fontSize={iconSize} />
                </IconButton>

                <Typography
                    component="span"
                    style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: dark ? '#fff' : '#000', fontSize: narrow ? '0.9rem' : '1rem' }}
                    title={row.name}
                >
                    {row.name}
                </Typography>

                <Chip
                    size="small"
                    label={isTheme ? 'Yours' : 'Built-in'}
                    style={{
                        marginLeft: 4,
                        marginRight: 4,
                        backgroundColor: isTheme ? '#1976d2' : (dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.10)'),
                        color: isTheme ? '#fff' : (dark ? '#fff' : '#000')
                    }}
                />

                {confirming ?
                    // Two-step delete, inline in this same popup - no second
                    // dialog file for one confirmation.
                    <>
                        <Button size="small" variant="contained" color="error" disabled={busy} onClick={() => onConfirmDelete(row)} style={{ textTransform: 'none', minWidth: 0 }}>
                            Remove
                        </Button>
                        <Button size="small" disabled={busy} onClick={onCancelDelete} style={{ textTransform: 'none', minWidth: 0, color: dark ? '#fff' : '#000' }}>
                            Keep
                        </Button>
                    </>
                    :
                    <>
                        {/* Kept alongside the drag handle on purpose: this is
                            the keyboard/screen-reader path, and the escape
                            hatch if dragging misbehaves on a device. Both
                            triggers funnel through the same onMove. */}
                        <IconButton size={buttonSize} style={buttonStyle} disabled={busy || index === 0} onClick={() => onMove(index, index - 1)} aria-label={"Move " + row.name + " up"} title="Move up">
                            <ArrowUpwardIcon fontSize={iconSize} />
                        </IconButton>
                        <IconButton size={buttonSize} style={buttonStyle} disabled={busy || index === total - 1} onClick={() => onMove(index, index + 1)} aria-label={"Move " + row.name + " down"} title="Move down">
                            <ArrowDownwardIcon fontSize={iconSize} />
                        </IconButton>
                        {/* THEME rows only: POST /container/delete answers 404
                            for a built-in platform id, so there is nothing to
                            offer there. */}
                        {isTheme ?
                            <IconButton size={buttonSize} style={buttonStyle} disabled={busy || deleteDisabled} onClick={() => onAskDelete(row)} aria-label={"Remove " + row.name} title={deleteDisabled ? "Save or cancel your new order first" : "Remove this card"}>
                                <DeleteOutlineIcon fontSize={iconSize} />
                            </IconButton>
                            :
                            <Box style={{ width: narrow ? 24 : 40 }} />}
                    </>
                }
            </Stack>
        </div>
    );
}

export const CardConfigPopup = ({ open, onClose, onSessionProblem }) => {
    const classes = useCardConfigStyles();
    const dispatch = useDispatch();

    const t = useSelector(state => state.themeswitch);
    const [theme, setTheme] = useState(t);

    useEffect(() => {
        setTheme(t);
    }, [t])

    const dark = theme === 'dark';

    const lang = useSelector(state => state.languageswitch);
    const language = containerLanguage(lang);

    // The WHOLE authentication.user object: usercontainer.service.js strips it
    // down to the five documented identity fields itself, so pre-trimming it
    // here (or passing bare strings) would break every call.
    const user = useSelector(state => state.authentication.user);

    const containers = useSelector(state => state.usercontainer.containers);
    const containersLoaded = useSelector(state => state.usercontainer.loaded);
    const creating = useSelector(state => state.usercontainer.creating);
    const loading = useSelector(state => state.usercontainer.loading);

    // This user's custom-card allowance, { used, allowed } or undefined when no
    // response has ever carried one. Named `allowance`, NOT `themes`: the
    // unrelated dark/light slice is state.themeswitch, already selected into
    // `t` / `theme` a few lines above, and `themes` next to `theme` would be a
    // one-character typo away from a silent bug.
    const allowance = useSelector(state => state.usercontainer.themes);

    const narrow = useMediaQuery('(max-width:420px)');

    const [rows, setRows] = useState([]);
    const [baseline, setBaseline] = useState('');
    const [title, setTitle] = useState('');
    const [titleError, setTitleError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [failure, setFailure] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [saving, setSaving] = useState(false);
    // The create itself cannot be cancelled (the 60s AbortController lives in
    // the service), so the loading popup's Cancel button only dismisses the
    // popup - the request carries on and still reports its outcome.
    const [loadingDismissed, setLoadingDismissed] = useState(false);

    // The latest open / pending-order state for the reseed effect below, which
    // must keep [containers] as its ONLY dependency: making these its
    // dependencies would re-run the reseed the instant a drag makes the list
    // dirty, which is precisely what the guard there exists to prevent.
    // Declared - and therefore run - BEFORE that effect, so a drag and a
    // server echo landing in the same commit are still seen in that order.
    const pendingRef = useRef({ open: false, dirty: false, baseline: '' });
    useEffect(() => {
        pendingRef.current = { open: open, dirty: orderKey(rows) !== baseline, baseline: baseline };
    }, [open, rows, baseline]);

    // Armed immediately before this popup's own create / delete / order
    // request. The echo that follows is the user's own action and must always
    // reseed, pending drag or not - replacing the dragged rows is the entire
    // point of a save. Consumed (disarmed) by the reseed effect below.
    //
    // A request that returns WITHOUT changing `containers` therefore has to
    // disarm it by hand, or the flag stays armed indefinitely and the next
    // background echo is swallowed instead - discarding a later drag silently,
    // which is exactly what this flag exists to prevent. Two kinds of path do
    // that and each clears the flag itself:
    //
    //  - the four handleFailure branches that return without calling resync()
    //    (container.title.invalid, container.title.reserved,
    //    container.limit.reached, container.identity.missing); the first three
    //    leave this popup open, and nothing in CREATE_FAILURE touches
    //    `containers`;
    //  - a 200 create whose body carries no `container`, since upsertContainer
    //    then returns the SAME `containers` reference and so produces no echo.
    //
    // Every other failure branch calls resync() and is consumed by its echo.
    // The one path left armed is a resync whose own GET then fails:
    // LIST_FAILURE leaves `containers` untouched, so the flag survives to
    // swallow one later echo. Pre-existing and not worth a second flag.
    const selfEchoRef = useRef(false);

    // Every change to `containers` is normally an authoritative server echo -
    // a list fetch, a create upsert, a delete removal or an order replacement
    // - so the displayed order is reseeded from it and any optimistic local
    // ordering is dropped. That is specifically what /containers/order
    // requires: its echo can append rows that were omitted and ignore rows it
    // cannot match, neither of which the client can predict.
    //
    // The one exception is a BACKGROUND refresh: Home.js's 20-minute tick and
    // Switch.js's "Load News" button both re-fetch /mycontainers now (theme
    // cards' news[] arrives only with that list), so an echo can land while
    // this popup is open with an unsaved drag. Reseeding then would discard
    // the drag silently, so it is kept instead. Entirely popup-local: no
    // Redux is touched, and the next Cancel / Save / close reseeds as usual.
    useEffect(() => {
        const next = toRows(containers);
        const nextKey = orderKey(next);

        if (selfEchoRef.current) {
            selfEchoRef.current = false;
        } else if (pendingRef.current.open && pendingRef.current.dirty) {
            if (nextKey !== pendingRef.current.baseline) {
                // The card list itself moved (another tab or device): say so
                // rather than either discarding the pending order or
                // pretending the popup is up to date.
                setNotice('Your cards changed elsewhere - close and reopen this window to see the latest.');
            }
            // Otherwise the server's order is unchanged and only the articles
            // were refreshed - which `rows` does not carry at all, so there is
            // nothing to tell the user about. Either way the pending order
            // stays on screen.
            //
            // Note what this return also skips: setConfirmDeleteId(null), so a
            // delete confirm opened before the echo is still standing after
            // it. That IS reachable - deleteDisabled={dirty} only disables the
            // "ask" button, while the confirm "Remove" button is
            // disabled={busy} alone and handleConfirmDelete has no dirty
            // guard - and confirming it discards the pending drag through the
            // delete echo. Left as is because that outcome is exactly what
            // happened before this guard existed: clearing the confirm here
            // would be a new behaviour, not a fix to a regression.
            return;
        }

        setRows(next);
        setBaseline(nextKey);
        setConfirmDeleteId(null);
    }, [containers]);

    // A fresh slate every time the popup is opened: no stale notice from the
    // last visit, no half-typed theme, no pending delete confirm. (The popup
    // stays mounted while closed so the create in flight - and its loading
    // popup - survive a close.)
    useEffect(() => {
        if (open) {
            setTitle('');
            setTitleError(null);
            setNotice(null);
            setFailure(null);
            setConfirmDeleteId(null);
        }
    }, [open]);

    const resync = useCallback(() => {
        dispatch(usercontainerActions.getMyContainers(user && user.uuid, language));
    }, [dispatch, user, language]);

    // Normally SocialMediaListRender has already fetched the list for this
    // user and language. This only covers the case where it has not, and
    // deliberately does NOT re-fetch on every open: a new `containers`
    // reference re-renders the merged card list, which resets CustomPagination
    // to page 1.
    useEffect(() => {
        if (open && !containersLoaded) {
            resync();
        }
    }, [open, containersLoaded, resync]);

    const dirty = useMemo(() => orderKey(rows) !== baseline, [rows, baseline]);
    // Still the CURRENT language's theme count, but no longer the gate: it only
    // feeds the copy that talks about this language's list ("one of them is in
    // another language"), and the fallback below.
    const themeCount = useMemo(() => rows.filter(r => r.kind === "THEME").length, [rows]);

    // The gate. `used` is the server's count across every language, so it
    // catches the case the old local count could not: five English plus five
    // German themes used to look like "five" here and let the user through to a
    // 409. With no allowance ever received this collapses to the previous
    // expression exactly - themeCount >= 10.
    const allowed = allowance ? allowance.allowed : FALLBACK_THEME_CEILING;
    const used = allowance ? allowance.used : themeCount;
    const atLimit = used >= allowed;
    const busy = creating || saving || loading;

    // The ONE reorder code path, shared by the drag handles and the up/down
    // buttons - one reducer, two triggers.
    const moveRow = useCallback((from, to) => {
        setRows(current => {
            if (from === to || from < 0 || to < 0 || from >= current.length || to >= current.length) {
                return current;
            }
            return arrayMove(current, from, to);
        });
        setNotice(null);
    }, []);

    // PointerSensor covers mouse, pen and touch in one listener (this is a
    // mobile-heavy app, so touch is not optional); the small distance
    // constraint stops a tap on the handle from being read as a drag.
    // KeyboardSensor + sortableKeyboardCoordinates give the same reorder from
    // the keyboard, since the handle is a focusable button.
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const from = rows.findIndex(r => r.id === active.id);
        const to = rows.findIndex(r => r.id === over.id);
        moveRow(from, to);
    };

    // Branches ONLY on the HTTP status code and body.errors[0].
    const handleFailure = useCallback((op, result) => {
        const status = result ? result.status : 0;
        const code = firstError(result && result.body);

        if (status === 403 && code === 'container.identity.mismatch') {
            // Not a validation error: the identity sent does not own these
            // rows. Revert the optimistic ordering, close, ask for a sign-in,
            // and resync so nothing stale is left behind.
            setRows(toRows(containers));
            setBaseline(orderKey(toRows(containers)));
            setConfirmDeleteId(null);
            resync();
            onClose();
            if (onSessionProblem) {
                onSessionProblem();
            }
            return;
        }
        if (status === 400 && code === 'container.identity.missing') {
            // This popup is login-gated, so an identity the backend rejects
            // means the session itself is the problem. Reverted exactly like
            // the 403 above: whatever happens to the session, a reopened
            // popup must not be showing a stale optimistic order.
            const fetched = toRows(containers);
            setRows(fetched);
            setBaseline(orderKey(fetched));
            setConfirmDeleteId(null);
            // Deliberately no resync(): a GET carrying an identity the backend
            // rejects would only re-fetch the anonymous list. onClose() below
            // means a reopened popup reseeds anyway, and a sign-in or LOGOUT
            // resets the slice. Nothing will echo, so disarm by hand.
            selfEchoRef.current = false;
            onClose();
            if (onSessionProblem) {
                onSessionProblem();
            }
            return;
        }
        if (status === 404 && code === 'container.notfound') {
            // Already gone (another tab or device). No error UI at all - just
            // resync to whatever the server has now. Keyed on the error code
            // as well as the status, so a 404 that is NOT the documented
            // "this row no longer exists" (a misrouted request, a gateway's
            // own 404 page) falls through to the generic branch below instead
            // of being silently swallowed.
            setConfirmDeleteId(null);
            resync();
            return;
        }
        // The three rejected-create branches below: nothing changed
        // server-side, there is nothing to resync, and the popup stays open so
        // the user can correct the theme or remove a card. No echo is coming,
        // so each one disarms the flag handleAdd armed - otherwise the next
        // background refresh would be eaten and a later drag lost with it.
        if (status === 400 && code === 'container.title.invalid') {
            selfEchoRef.current = false;
            setTitleError('That theme cannot be used. Please use 1 to ' + MAX_TITLE + ' ordinary characters.');
            return;
        }
        if (status === 400 && code === 'container.title.reserved') {
            selfEchoRef.current = false;
            setTitleError('That is the name of a built-in card. Please choose another theme.');
            return;
        }
        if (status === 409 && code === 'container.limit.reached') {
            selfEchoRef.current = false;
            // No `themes` on this body - no error body at any status carries
            // one - so this names the last allowance we held, and names ONLY
            // `allowed`. Deliberately never `used`: /mycontainers resolves the
            // caller by uuid while create resolves by email, so a drifted uuid
            // can legitimately leave a 0 on screen while the server refuses the
            // create. Quoting that 0 back here ("you have 0 of 1") would read
            // as a bug; stating the allowance alone is true either way.
            // "Remove one first" cannot help at an allowance of 0, so that case
            // reuses the counter's own wording for it instead.
            setNotice(!allowance
                ? 'You have reached the number of custom cards your account allows. Remove one first - it counts your cards in every language together.'
                : allowed === 0
                    ? 'Your account does not currently allow custom cards.'
                    : 'Your account allows ' + allowed + ' custom ' + (allowed === 1 ? 'card' : 'cards') + '. Remove one first - the allowance counts your cards in every language together.');
            return;
        }
        if (status === 400 && (code === 'container.id.missing' || code === 'container.order.empty')) {
            // Nothing the user did: the request left here incomplete.
            console.error('CardConfigPopup: incomplete ' + op + ' request rejected by the server (' + code + ')');
            setFailure('Something went wrong here. Please close this window and open it again.');
            resync();
            return;
        }
        // 500, and anything unmapped. status 0 is the service's network/CORS
        // marker, not an HTTP status.
        setFailure(status === 0
            ? "Can't reach OneLike. Please check your connection and try again."
            : 'Something went wrong on our side. Please try again.');
        resync();
    }, [allowance, allowed, containers, onClose, onSessionProblem, resync]);

    const handleAdd = () => {
        // Blocked while an unsaved reorder is pending: a create echoes a
        // container, which reseeds the rows from the server and would silently
        // throw the pending order away. Better to ask than to lose it.
        if (busy || atLimit || dirty) {
            return;
        }
        const trimmed = title.trim();
        const problem = titleProblem(trimmed);
        if (problem) {
            setTitleError(problem);
            return;
        }
        setTitleError(null);
        setNotice(null);
        setFailure(null);
        setLoadingDismissed(false);
        selfEchoRef.current = true;

        dispatch(usercontainerActions.create(user, trimmed, language)).then(result => {
            if (result.outcome === USERCONTAINER_UNKNOWN_OUTCOME) {
                // Aborted at 60s, or the network dropped: POST /container may
                // well have committed the row anyway, so this is explicitly
                // NOT reported as a failure. Re-fetch and let the list answer.
                // Deliberately no auto-retry: it is idempotent and therefore
                // safe, but it costs another minute.
                setTitle('');
                setNotice('This is taking longer than expected; your card may still appear.');
                resync();
                return;
            }
            if (result.ok) {
                if (!(result.body && result.body.container)) {
                    // CREATE_SUCCESS upserts body.container, and
                    // upsertContainer returns the SAME `containers` reference
                    // when there is no container to upsert - so this success
                    // produces no echo and has to disarm the flag itself.
                    selfEchoRef.current = false;
                }
                setTitle('');
                if (result.body && result.body.created === false) {
                    // Duplicate title for this user and language: the server
                    // echoed the EXISTING card. Idempotent success, not an error.
                    setNotice('You already have a "' + trimmed + '" card in ' + language + '.');
                    return;
                }
                // The server front-inserts a new theme at stored position 0 and
                // shifts the rest +1, so the list this create just echoed is
                // NOT the list the server now holds: upsertContainer appends the
                // container to whatever it already had and cannot know the
                // insertion rule. Re-read /mycontainers and let the server's
                // order be what shows.
                //
                // Deliberately not a local prepend. That would put a
                // server-owned ordering rule in the client, where it goes stale
                // silently, and it contradicts two invariants already written
                // down in this repo: usercontainer.reducer.js:3-5 (`containers`
                // is "always REPLACED wholesale from a server echo - never
                // merged with, or patched from, an optimistic local ordering")
                // and SocialMediaListRender.js:109-110 ("Array order is
                // authoritative: it is never sorted on, persisted or keyed
                // off"). It would also need a carve-out so a duplicate does not
                // move.
                //
                // The arm below is DEFENSIVE, and under React 18 createRoot
                // (index.js:17) it is currently a NO-OP. Passive effects flush
                // on a Scheduler macrotask, while this .then continuation is a
                // microtask, and there is no macrotask boundary between the
                // dispatch above and this line - so the reseed effect has not
                // run yet, the arm handleAdd set before dispatching (:652) is
                // still unconsumed, and it is the create echo that consumes
                // it. The resync echo lands UNARMED. Written anyway because it
                // costs nothing and would be the thing that matters if that
                // flush ordering ever changed.
                //
                // What actually delivers the resync echo's reseed is not an arm
                // but the not-dirty fall-through in the reseed effect above: at
                // :431 an unarmed echo is only diverted when the popup is open
                // AND `rows` is dirty, so a clean list falls through to
                // setRows/setBaseline. That is load-bearing, and it holds
                // because `rows` CANNOT be dirty in this window:
                //  - handleAdd early-returns on `dirty` (:639), so the rows are
                //    clean at request time;
                //  - moveRow (:507) is the SOLE writer that can separate `rows`
                //    from `baseline`, with exactly two triggers: handleDragEnd
                //    (:527-534) and the up/down buttons (:306, :309);
                //  - all three are gated on `busy` - the buttons via
                //    disabled={busy || ...}, and the drag via its single
                //    activator, the handle IconButton at :254-259 which carries
                //    {...attributes} {...listeners} ref={setActivatorNodeRef}
                //    AND disabled={busy} (a disabled native button fires no
                //    pointer or keyboard activation);
                //  - busy = creating || saving || loading (:503), and `creating`
                //    is true from CREATE_REQUEST for the whole in-flight window.
                //
                // No arm on the UNKNOWN_OUTCOME branch above, and the
                // difference is NOT the arm - that one is unconsumed there too.
                // It is that CREATE_UNKNOWN leaves `containers` untouched
                // (usercontainer.reducer.js:109-113), so that branch produces no
                // echo at all and its resync's echo is the one the arm covers.
                //
                // The cost, stated plainly: this opts the call site into the
                // same residual every other resync() here already carries (last
                // paragraph of the selfEchoRef comment above) - if the GET
                // fails, LIST_FAILURE leaves the flag armed and one later
                // background echo is swallowed.
                //
                // Nothing like this on the created === false branch above: a
                // duplicate returns the existing card at its existing position,
                // so nothing moved and there is nothing to re-read.
                //
                // The second reseed of rows/baseline will read as redundancy,
                // because today it is equivalent data - but once the backend
                // lands it is precisely the reseed that changes this popup's row
                // order (the create echo appends the theme last; the GET returns
                // it first). That is the feature, not a redundancy - and it
                // arrives through the same not-dirty fall-through, not an arm.
                selfEchoRef.current = true;
                resync();
                const added = result.body && result.body.container;
                if (added && Array.isArray(added.news) && added.news.length === 0) {
                    // 200 with news: [] is a legitimate success - every
                    // upstream proxy was down when the card was built.
                    setNotice('"' + trimmed + '" has been added. No articles yet.');
                    return;
                }
                setNotice('"' + trimmed + '" has been added to your ' + language + ' cards.');
                return;
            }
            handleFailure('create', result);
        });
    };

    const handleConfirmDelete = (row) => {
        if (busy || !row || row.kind !== "THEME" || row.containerId === undefined) {
            return;
        }
        setNotice(null);
        setFailure(null);
        selfEchoRef.current = true;

        dispatch(usercontainerActions.deleteContainer(user, row.containerId)).then(result => {
            if (result.ok) {
                // The reducer has already dropped the row from `containers`,
                // which reseeds the list through the effect above.
                setConfirmDeleteId(null);
                setNotice('"' + row.name + '" has been removed.');
                return;
            }
            if (result.status === 404 && firstError(result.body) === 'container.notfound') {
                // Already deleted elsewhere: remove it locally, show no error,
                // then resync. Status AND code, same as handleFailure - any
                // other 404 is not "this row is gone" and belongs in the
                // generic branch.
                setRows(current => current.filter(r => r.id !== row.id));
                setConfirmDeleteId(null);
                resync();
                return;
            }
            handleFailure('delete', result);
        });
    };

    const handleSave = () => {
        if (busy || !dirty) {
            return;
        }
        setNotice(null);
        setFailure(null);
        setSaving(true);
        // The rows ARE dirty here by definition, so the reseed effect's guard
        // would otherwise refuse the one echo that must always be applied.
        selfEchoRef.current = true;

        // The full ordered row list goes in: the service projects it down to
        // [{ kind, name }] itself. Omitted rows would be appended, never
        // deleted, so sending all of them is what keeps the order exact.
        dispatch(usercontainerActions.order(user, language, rows)).then(result => {
            setSaving(false);
            if (result.ok) {
                // ORDER_SUCCESS has already replaced `containers` from the
                // echo, and the effect above reseeds the rows from that - so
                // what stays on screen is the server's order, not the dragged
                // one.
                setNotice('Your card order has been saved.');
                return;
            }
            handleFailure('order', result);
        });
    };

    const handleCancel = () => {
        // Cancel discards: back to the fetched order, nothing sent.
        const next = toRows(containers);
        setRows(next);
        setBaseline(orderKey(next));
        setTitle('');
        setTitleError(null);
        setNotice(null);
        setFailure(null);
        setConfirmDeleteId(null);
        onClose();
    };

    const handleTitleChange = (e) => {
        setTitle(e.target.value);
        setTitleError(null);
    };

    const handleTitleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    const textColor = dark ? '#fff' : '#000';
    const mutedColor = dark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)';
    const surface = dark ? '#0D1F2D' : '#fff';

    return (
        <div>
            <Dialog
                open={open}
                onClose={busy ? undefined : handleCancel}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
                classes={{ paper: classes.paper }}
            >
                <DialogTitle style={{ cursor: 'move', color: textColor, background: surface }} id="draggable-dialog-title">
                    <span>Your Social News cards</span>
                </DialogTitle>
                {/* Everything interactive lives in DialogContent, which the
                    PaperComponent's `cancel` selector excludes from the
                    dialog's own dragger - that is what stops the row drag and
                    the dialog drag from fighting. */}
                <DialogContent style={{ backgroundColor: surface, color: textColor, overflowY: 'auto', paddingLeft: narrow ? 8 : 16, paddingRight: narrow ? 8 : 16 }}>
                    <Box style={{ paddingTop: 8, paddingBottom: 8 }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                            <TextField
                                id="cardconfig-title"
                                label="Add a theme"
                                placeholder="Climate, Formula 1, My town..."
                                value={title}
                                onChange={handleTitleChange}
                                onKeyDown={handleTitleKeyDown}
                                error={!!titleError}
                                helperText={titleError || ' '}
                                size="small"
                                fullWidth
                                disabled={busy || atLimit || dirty}
                                inputProps={{ maxLength: MAX_TITLE, autoComplete: 'off', spellCheck: false }}
                                sx={{
                                    '& .MuiInputBase-input': { color: textColor },
                                    '& .MuiInputLabel-root': { color: mutedColor },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={handleAdd}
                                disabled={busy || atLimit || dirty}
                                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                style={{ marginTop: 2, textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                                {creating ? 'Adding' : 'Add'}
                            </Button>
                        </Stack>
                        <Typography variant="body2" style={{ color: mutedColor }}>
                            A card belongs to the language it was created in, so this one will appear in your {language} Social News only.
                        </Typography>
                        <Typography variant="body2" style={{ color: mutedColor }}>
                            {allowanceSummary(allowance, allowed, used, themeCount, language)}
                        </Typography>
                        <Typography variant="body2" style={{ color: mutedColor }}>
                            A brand new theme can take up to a minute to collect its first articles.
                        </Typography>
                    </Box>

                    {notice ?
                        <Typography variant="body2" style={{ color: textColor, background: dark ? 'rgba(25,118,210,0.22)' : 'rgba(25,118,210,0.10)', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                            {notice}
                        </Typography>
                        : null}
                    {failure ?
                        <Typography variant="body2" style={{ color: '#fff', background: '#d32f2f', borderRadius: 6, padding: 8, marginBottom: 8 }}>
                            {failure}
                        </Typography>
                        : null}

                    <Typography variant="body2" style={{ color: mutedColor, marginBottom: 4 }}>
                        Drag the handle, or use the arrows, to change the order of your cards. Only cards you added can be removed.
                    </Typography>
                    {dirty ?
                        <Typography variant="body2" style={{ color: textColor, marginBottom: 4 }}>
                            Your new order is not saved yet - use "Save order" below, or "Cancel" to discard it. Adding and removing cards is paused until then.
                        </Typography>
                        : null}

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={rows.map(r => r.id)} strategy={verticalListSortingStrategy}>
                            {rows.map((row, index) => (
                                <CardRow
                                    key={row.id}
                                    row={row}
                                    index={index}
                                    total={rows.length}
                                    narrow={narrow}
                                    dark={dark}
                                    busy={busy}
                                    deleteDisabled={dirty}
                                    confirming={confirmDeleteId === row.id}
                                    onMove={moveRow}
                                    onAskDelete={(r) => { setConfirmDeleteId(r.id); setNotice(null); setFailure(null); }}
                                    onCancelDelete={() => setConfirmDeleteId(null)}
                                    onConfirmDelete={handleConfirmDelete}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>

                    {rows.length === 0 ?
                        <Typography variant="body2" style={{ color: mutedColor }}>
                            Your cards are not loaded yet.
                        </Typography>
                        : null}
                </DialogContent>
                <DialogActions style={{ color: '#fff', background: surface }} > {/* light */}
                    <Button onClick={handleCancel} variant="contained" disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" disabled={busy || !dirty}>
                        {saving ? 'Saving' : 'Save order'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* POST /container does a live Google News fetch for a theme nobody
                has created before, so the wait is real. Deliberately NOT given
                a zIndex above: DraggableLoadingPopup runs at MUI's default
                modal zIndex, and it is mounted after this dialog, so it paints
                on top only as long as this dialog stays at the default too. */}
            <DraggableLoadingPopup openLoadingPopup={!!creating && !loadingDismissed} handleCloseLoadingPopup={() => setLoadingDismissed(true)} />
        </div>
    );
}
